import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios from 'axios';

import {
  requestCreatePost,
  requestDeletePost,
  requestSearchPosts,
  requestUpdatePost,
  requestViewPostHierarchy,
  requestViewPosts
} from './post.actions';
import {
  getCreatePostRequest,
  getDeletePostRequest,
  getSearchPostsRequest,
  getUpdatePostRequest,
  getViewPostHierarchyRequest,
  getViewPostsRequest
} from './post.endpoints';

import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';
import {
  addMemberPosts,
  addPosts,
  replacePost,
  setMemberPosts,
  setPostHierarchy,
  setPosts,
  setPostSearchResults
} from '../../slices/programme/tribe/tribe.slice';

import {
  CREATING_POST,
  DELETING_POST,
  RETRIEVING_POST,
  RETRIEVING_POSTS,
  SEARCHING_POSTS,
  UPDATING_POST
} from './post.constants';
import { ERROR, SUCCESS } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';

export function* performCreatePostRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(addSpinner(CREATING_POST));

    const [endpoint, options] = getCreatePostRequest(input);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner(CREATING_POST));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(CREATING_POST));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForCreatePostRequest() {
  yield takeLatest(requestCreatePost.type, performCreatePostRequest);
}

export function* performViewPostsRequest({ payload }) {
  const { programmeExternalReference, page, size, searchType, ammend, replace, memberExternalReference, onSuccess } =
    payload;

  try {
    if (!ammend && !replace) {
      yield put(addSpinner(RETRIEVING_POSTS));
    }

    const [endpoint, options] = getViewPostsRequest(
      programmeExternalReference,
      page,
      size,
      searchType,
      memberExternalReference
    );

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    if (ammend && memberExternalReference) {
      yield put(addMemberPosts(data.posts));
    } else if (ammend && !memberExternalReference) {
      yield put(addPosts(data.posts));
    } else if (replace) {
      yield put(replacePost({ page, posts: data.posts }));
    } else if (memberExternalReference) {
      yield put(setMemberPosts(data.posts));
    } else {
      yield put(setPosts(data.posts));
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    if (!ammend && !replace) {
      yield put(removeSpinner(RETRIEVING_POSTS));
    }
  }
}

export function* watchForViewPostsRequest() {
  yield takeLatest(requestViewPosts.type, performViewPostsRequest);
}

export function* performUpdatePostRequest({ payload }) {
  const { input, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_POST));

    const [endpoint, options] = getUpdatePostRequest(input);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner(UPDATING_POST));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(UPDATING_POST));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForUpdatePostRequest() {
  yield takeLatest(requestUpdatePost.type, performUpdatePostRequest);
}

export function* performDeletePostRequest({ payload }) {
  const { postExternalReference, onSuccess } = payload;

  try {
    yield put(addSpinner(DELETING_POST));

    const [endpoint, options] = getDeletePostRequest(postExternalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(removeSpinner(DELETING_POST));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(DELETING_POST));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForDeletePostRequest() {
  yield takeLatest(requestDeletePost.type, performDeletePostRequest);
}

export function* performSearchPostsRequest({ payload }) {
  const { programmeExternalReference, memberExternalReference, searchValue, onSuccess } = payload;

  try {
    yield put(addSpinner(SEARCHING_POSTS));

    const [endpoint, options] = getSearchPostsRequest(programmeExternalReference, memberExternalReference, searchValue);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(setPostSearchResults(data.postSearchResults));
    yield put(removeSpinner(SEARCHING_POSTS));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(SEARCHING_POSTS));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForSearchPostsRequest() {
  yield takeLatest(requestSearchPosts.type, performSearchPostsRequest);
}

export function* performViewPostHierarchyRequest({ payload }) {
  const { externalReference, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_POST));

    const [endpoint, options] = getViewPostHierarchyRequest(externalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }

    yield put(setPostHierarchy(data.post));
    yield put(removeSpinner(RETRIEVING_POST));
    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(RETRIEVING_POST));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForViewPostHierarchyRequest() {
  yield takeLatest(requestViewPostHierarchy.type, performViewPostHierarchyRequest);
}

export default function* postSaga() {
  yield all([
    watchForCreatePostRequest(),
    watchForViewPostsRequest(),
    watchForUpdatePostRequest(),
    watchForDeletePostRequest(),
    watchForSearchPostsRequest(),
    watchForViewPostHierarchyRequest()
  ]);
}
